// PastReadingsScreen - View historical readings
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Trash2, CheckSquare, ChevronDown } from 'lucide-react';
import { Button } from '../common/Button';
import { PageContainer, GlassCard } from '../layout/PageContainer';
import { CardSpread } from '../tarot/TarotCard';
import { PageLoader } from '../common/LoadingSpinner';
import { MarkdownText } from '../common/MarkdownText';

export const PastReadingsScreen = ({
    readings,
    selectedPastReading,
    onSelectReading,
    isLoading,
    onClearHistory,
    onCardClick,
    onGetActionableSteps,
    currentUser
}) => {
    const isAnonymous = currentUser?.isAnonymous;
    const displayReadings = isAnonymous ? readings.slice(0, 3) : readings;

    if (isLoading && readings.length === 0 && !selectedPastReading) {
        return (
            <PageContainer className="py-8">
                <PageLoader text="Loading past journeys..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer className="py-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        className="inline-block mb-3"
                        animate={{ rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 4, repeat: Infinity }}
                    >
                        <BookOpen className="w-10 h-10 text-theme-accent mx-auto" />
                    </motion.div>
                    <h2 className="text-3xl sm:text-4xl font-serif text-theme">
                        Echoes of the Cosmos
                    </h2>
                </div>

                {/* Anonymous user notice */}
                {isAnonymous && readings.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6"
                    >
                        <GlassCard className="text-center py-3 border-theme-glass">
                            <p className="text-sm text-theme-muted">
                                Only showing 3 most recent echoes.
                                <br />
                                <span className="text-theme-secondary">Sign in to preserve all your cosmic journeys!</span>
                            </p>
                        </GlassCard>
                    </motion.div>
                )}

                {/* Empty state */}
                {!isLoading && readings.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-12"
                    >
                        <BookOpen className="w-16 h-16 text-theme-muted mx-auto mb-4" />
                        <h3 className="text-xl text-theme-secondary mb-2">
                            Your Akashic records are clear
                        </h3>
                        <p className="text-theme-muted">
                            No past readings found. Time to start a new cosmic journey!
                        </p>
                    </motion.div>
                )}

                {/* Readings list */}
                <div className="space-y-4">
                    <AnimatePresence>
                        {displayReadings.map((reading, index) => (
                            <ReadingCard
                                key={reading.id}
                                reading={reading}
                                isExpanded={selectedPastReading?.id === reading.id}
                                onToggle={() => onSelectReading(
                                    reading.id === selectedPastReading?.id ? null : reading
                                )}
                                onCardClick={onCardClick}
                                onGetActionableSteps={onGetActionableSteps}
                                index={index}
                            />
                        ))}
                    </AnimatePresence>
                </div>

                {/* Clear history button */}
                {readings.length > 0 && (
                    <motion.div
                        className="mt-8 flex justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={onClearHistory}
                            icon={Trash2}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Clearing...' : 'Clear All History'}
                        </Button>
                    </motion.div>
                )}
            </motion.div>
        </PageContainer>
    );
};

// Individual reading card
const ReadingCard = ({
    reading,
    isExpanded,
    onToggle,
    onCardClick,
    onGetActionableSteps,
    index
}) => {
    const formattedDate = reading.timestamp
        ? new Date(reading.timestamp.seconds * 1000).toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
        : 'Unknown date';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ delay: index * 0.05 }}
        >
            <GlassCard
                className={`cursor-pointer transition-all ${isExpanded ? 'border-amber-500/40' : 'border-white/10'
                    }`}
                onClick={onToggle}
                hoverable
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <p className="text-theme font-medium line-clamp-2 break-words">
                            "{reading.question}"
                        </p>
                        <p className="text-xs text-theme-muted mt-1">
                            {formattedDate}
                        </p>
                    </div>
                    <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <ChevronDown className="w-5 h-5 text-theme-accent flex-shrink-0" />
                    </motion.div>
                </div>

                {/* Expanded content */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="pt-4 mt-4 border-t border-white/10">
                                {/* Cards */}
                                <h4 className="text-sm font-serif text-theme-secondary mb-3 text-center">
                                    Cards Revealed
                                </h4>
                                {reading.cards?.length > 0 ? (
                                    <CardSpread
                                        cards={reading.cards}
                                        onCardClick={onCardClick}
                                    />
                                ) : (
                                    <p className="text-theme-muted text-center text-sm">
                                        Card data unavailable
                                    </p>
                                )}

                                {/* Interpretation */}
                                <div className="mt-4">
                                    <h4 className="text-sm font-serif text-theme-secondary mb-2">
                                        The Cosmos Whispered:
                                    </h4>
                                    <MarkdownText size="sm" className="text-theme">
                                        {reading.interpretation || ''}
                                    </MarkdownText>
                                </div>

                                {/* Actionable steps button */}
                                <div className="mt-4 flex justify-center">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={onGetActionableSteps}
                                        icon={CheckSquare}
                                        className="border-teal-500/30"
                                    >
                                        View Actionable Steps
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </GlassCard>
        </motion.div>
    );
};

export default PastReadingsScreen;
